//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { IScope } from 'angular';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { InlineSpinner } from '@components/atoms/spinner/Spinner';
import { SubsectionText } from '@components/atoms/text/Text';
import makeAngularReactControllerWithScope from '@react-bridge/angular-react-widget-with-scope';

export interface IntelligentProductTaggingSearchLoaderProps {
  isLoading: boolean;
}

const LOADER_STEPS = [
  { delay: 0, message: 'Scanning image…' },
  { delay: 4000, message: 'Detecting objects…' },
  { delay: 10000, message: 'Identifying products…' },
  { delay: 17000, message: 'Finalizing…' },
  {
    delay: 20000,
    message: 'Almost there. This is taking a bit longer than usual...',
  },
] as const;

const IntelligentProductTaggingSearchLoader = ({
  isLoading,
}: IntelligentProductTaggingSearchLoaderProps) => {
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (!isLoading) {
      setStepIndex(0);
      return;
    }

    /* eslint-disable react-web-api/no-leaked-timeout -- Timeouts are properly cleaned up via timers.forEach(clearTimeout) below, but the rule can't trace setTimeout through .map() and .forEach(clearTimeout) patterns */
    const timers = LOADER_STEPS.slice(1).map((step, index) =>
      setTimeout(() => setStepIndex(index + 1), step.delay),
    );

    return () => timers.forEach(clearTimeout);
    /* eslint-enable react-web-api/no-leaked-timeout */
  }, [isLoading]);

  const fallbackStep = LOADER_STEPS[LOADER_STEPS.length - 1];

  if (!isLoading || !fallbackStep) {
    return null;
  }

  const currentStep = LOADER_STEPS[stepIndex] ?? fallbackStep;

  return (
    <SpinnerContainer>
      <StyledInlineSpinner />
      <SubsectionText>{currentStep.message}</SubsectionText>
    </SpinnerContainer>
  );
};

angular.module('uia').component('intelligentProductTaggingSearchLoader', {
  bindings: {
    isLoading: '<',
  },
  controller: makeAngularReactControllerWithScope(
    (
      scope: {
        $ctrl: IntelligentProductTaggingSearchLoaderProps;
      } & IScope,
    ) => {
      return (
        <IntelligentProductTaggingSearchLoader
          isLoading={scope.$ctrl.isLoading}
        />
      );
    },
  ),
});

const SpinnerContainer = styled.div(
  ({ theme }) => `
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacings[1]};
  padding: ${theme.spacings[1]} ${theme.spacings[2]};
  margin-bottom: ${theme.spacings[3]};
`,
);

const StyledInlineSpinner = styled(InlineSpinner)(
  ({ theme }) => `
  width: ${theme.spacings[4]};
  height: ${theme.spacings[4]};
`,
);
